import { set } from 'date-fns';
import { DateTimeSchema } from './ChallengeForm.schema';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { EnsuredSubtype, SMap } from '../../../helpers/types';
import { FieldErrors } from 'react-hook-form';

export type DateTime = EnsuredSubtype<
  DateTimeSchema,
  'dateTimestamp' | 'dateDayTime' | 'dateHour' | 'dateMinute'
>;

// from form to server time
export const createDate = (p?: DateTime): Date => {
  if (!p || p.dateTimestamp == null) {
    return new Date('1970-01-01T00:00:00Z');
  }
  const userTimezone = getUserTZ();
  let hours = p.dateHour ?? 0;
  if (p.dateDayTime === 'PM' && hours !== 12) hours += 12;
  else if (p.dateDayTime === 'AM' && hours === 12) hours = 0;

  const localBase = utcToZonedTime(new Date(p.dateTimestamp), userTimezone);
  const adjustedDate = set(localBase, {
    hours,
    minutes: p.dateMinute ?? 0,
  });

  return zonedTimeToUtc(adjustedDate, userTimezone);
};

export function getUserTZ(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// from server time to form
export const toDateTimeSchema = (date: Date): DateTimeSchema => {
  const tz = getUserTZ();
  const localDate = utcToZonedTime(date, tz);
  const hours24 = localDate.getHours();
  let dateHour: number;
  let dateDayTime: 'AM' | 'PM';

  if (hours24 === 0) {
    dateHour = 12;
    dateDayTime = 'AM';
  } else if (hours24 > 12) {
    dateHour = hours24 - 12;
    dateDayTime = 'PM';
  } else if (hours24 === 12) {
    dateHour = 12;
    dateDayTime = 'PM';
  } else {
    dateHour = hours24;
    dateDayTime = 'AM';
  }

  return {
    dateTimestamp: date.getTime(),
    dateHour,
    dateMinute: localDate.getMinutes(),
    dateDayTime,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toErrors = <T extends SMap<any>>(e: FieldErrors<T>) => {
  const messages = Object.keys(e)
    .map((v) => e[v]?.message)
    .filter(Boolean) as string[];

  if (messages.length === 0) return null;
  return {
    title:
      messages.length > 1 ? 'There are several problems' : 'There is a problem',
    messages,
  };
};

export function getAllFieldPaths<T extends object>(
  obj: T,
  prefix = '',
): string[] {
  const paths: string[] = [];

  for (const [key, value] of Object.entries(obj)) {
    const currentPath = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      paths.push(...getAllFieldPaths(value, currentPath));
    } else {
      paths.push(currentPath);
    }
  }
  return paths;
}
